import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Divider,
    Avatar,
    Card,
    CardContent,
    Stack,
    Chip,
    Grid,
    CircularProgress
} from '@mui/material';
import PropTypes from 'prop-types';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';




import { validateDiscountCode } from '../api/discountCodeApi';
import { formatCurrency } from '../utils/currency';

export default function PaymentForm({ affiliateCredit, appliedCredit, setAppliedCredit, planPrice, affiliateInfo, planName, discountCode = '', setDiscountCode, appliedDiscount, setAppliedDiscount, affiliateId, planId, isPremiumAffiliate = true }) {
    const [creditInput, setCreditInput] = useState('0');
    const [errorMessage, setErrorMessage] = useState('');
    const [discountInput, setDiscountInput] = useState(discountCode);
    const [discountChecking, setDiscountChecking] = useState(false);

    // Price after discount (credit must not exceed this)
    const priceAfterDiscount = planPrice - (appliedDiscount?.valid ? (appliedDiscount?.discountAmount || 0) : 0);
    const maxApplicableCredit = Math.min(affiliateCredit, Math.max(0, priceAfterDiscount));
    const isPremium = isPremiumAffiliate === true;
    const paymentCurrency = affiliateInfo?.paymentCurrency || "EUR";
    const creditHelperMessage = errorMessage || `Available: ${formatCurrency(maxApplicableCredit, paymentCurrency)}`;

    // Update input field when appliedCredit changes
    useEffect(() => {
        setCreditInput(appliedCredit.toString());
    }, [appliedCredit]);

    // When discount is applied, cap credit to new max so total never goes negative
    useEffect(() => {
        if (appliedCredit > maxApplicableCredit) {
            setAppliedCredit(maxApplicableCredit);
            setCreditInput(maxApplicableCredit.toString());
        }
    }, [maxApplicableCredit, appliedCredit]);

    useEffect(() => {
        setDiscountInput(discountCode);
    }, [discountCode]);

    const handleApplyDiscount = async () => {
        const code = discountInput?.trim();
        if (!code || !affiliateId) return;
        setDiscountChecking(true);
        setAppliedDiscount?.(null);
        try {
            const result = await validateDiscountCode(code, affiliateId, planPrice, planId);
            setAppliedDiscount?.(result);
            setDiscountCode?.(code);
            if (!result.valid) setErrorMessage(result.message || 'Discount code is not valid');
            else setErrorMessage('');
        } catch (err) {
            setAppliedDiscount?.({ valid: false });
            setErrorMessage('Failed to validate discount code');
        } finally {
            setDiscountChecking(false);
        }
    };

    const handleRemoveDiscount = () => {
        setDiscountInput('');
        setDiscountCode?.('');
        setAppliedDiscount?.(null);
        setErrorMessage('');
    };

    const handleCreditInputChange = (event) => {
        const value = event.target.value;
        setCreditInput(value);

        // Validate input
        if (value === '') {
            setErrorMessage('');
            return;
        }

        const numValue = Number(value);
        if (isNaN(numValue)) {
            setErrorMessage('Please enter a number');
        } else if (numValue < 0) {
            setErrorMessage('Credit cannot be negative');
        } else if (numValue > affiliateCredit) {
            setErrorMessage(`Maximum available credit is ${formatCurrency(affiliateCredit, paymentCurrency)}`);
        } else if (numValue > maxApplicableCredit) {
            setErrorMessage(`Maximum credit can be ${formatCurrency(maxApplicableCredit, paymentCurrency)} (after discount)`);
        } else {
            setErrorMessage('');
        }
    };

    // Apply entered credit immediately
    const applyCredit = () => {
        if (creditInput === '' || errorMessage) return;

        const numValue = Number(creditInput);
        if (!isNaN(numValue) && numValue >= 0 && numValue <= maxApplicableCredit) {
            // Use exact value
            setAppliedCredit(numValue);
        }
    };

    // Button to apply maximum credit (capped by price after discount)
    const applyMax = () => {
        setCreditInput(maxApplicableCredit.toString());
        setAppliedCredit(maxApplicableCredit);
        setErrorMessage('');
    };



    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 2,
                maxHeight: 'none',
                overflow: 'visible'
            }}
        >
            <CardContent
                sx={{
                    p: { xs: 2, sm: 3 },
                    pt: 0,
                    overflow: 'visible'
                }}
            >
                {/* Header with affiliate name and logo */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2
                    }}
                >
                    <Typography variant="h5" fontWeight="600">
                        {affiliateInfo.name}
                    </Typography>
                    <Avatar
                        src={affiliateInfo.logo}
                        sx={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: 'transparent',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                        }}
                        variant="rounded"
                    />
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Payment summary */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 2,
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderColor: 'divider',
                        overflow: 'visible'
                    }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Order Summary
                            </Typography>
                        </Grid>
                        <Grid item xs={8}>
                            <Typography variant="body2">
                                {planName}
                            </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" fontWeight="500">
                                {formatCurrency(planPrice, paymentCurrency)}
                            </Typography>
                        </Grid>

                        {appliedDiscount?.valid && appliedDiscount?.discountAmount > 0 && (
                            <>
                                <Grid item xs={8}>
                                    <Typography variant="body2" color="success.main">
                                        Discount code
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} sx={{ textAlign: 'right' }}>
                                    <Typography variant="body2" fontWeight="500" color="success.main">
                                        -{formatCurrency(appliedDiscount.discountAmount, paymentCurrency)}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        {appliedCredit > 0 && (
                            <>
                                <Grid item xs={8}>
                                    <Typography variant="body2" color="success.main">
                                        Credit applied
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} sx={{ textAlign: 'right' }}>
                                    <Typography variant="body2" fontWeight="500" color="success.main">
                                        -{formatCurrency(appliedCredit, paymentCurrency)}
                                    </Typography>
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                        </Grid>

                        <Grid item xs={8}>
                            <Typography variant="subtitle1" fontWeight="600">
                                Total
                            </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle1" fontWeight="600">
                                {formatCurrency(
                                    planPrice - appliedCredit - (appliedDiscount?.valid ? (appliedDiscount?.discountAmount || 0) : 0),
                                    paymentCurrency
                                )}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Discount code */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        Discount code
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <TextField
                            size="small"
                            placeholder="Enter discount code"
                            value={discountInput}
                            onChange={(e) => setDiscountInput(e.target.value)}
                            disabled={!!(appliedDiscount?.valid && appliedDiscount?.discountAmount > 0)}
                            sx={{ minWidth: 160, flex: 1 }}
                            error={!!(appliedDiscount && !appliedDiscount.valid)}
                            helperText={appliedDiscount && !appliedDiscount.valid ? appliedDiscount.message : ''}
                        />
                        {appliedDiscount?.valid && appliedDiscount?.discountAmount > 0 ? (
                            <Button variant="outlined" size="small" color="secondary" onClick={handleRemoveDiscount}>
                                Remove
                            </Button>
                        ) : (
                            <Button variant="outlined" size="small" onClick={handleApplyDiscount} disabled={discountChecking || !discountInput?.trim()}>
                                {discountChecking ? 'Checking...' : 'Apply'}
                            </Button>
                        )}
                    </Box>
                    {appliedDiscount?.valid && appliedDiscount?.discountAmount > 0 && (
                        <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                            {appliedDiscount.message}
                        </Typography>
                    )}
                </Box>

                <Typography variant="h6" gutterBottom sx={{ fontWeight: '500' }}>
                    Credit and payment
                </Typography>

                <Box sx={{ mb: 3, overflow: 'visible' }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: 'primary.lighter',
                        color: 'primary.dark'
                    }}>
                        <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: '500' }}>
                            Your available credit: <Box component="span" sx={{ fontWeight: '600' }}>{formatCurrency(affiliateCredit, paymentCurrency)}</Box>
                        </Typography>
                    </Box>

                    {!isPremium && (
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                mb: 2,
                                borderRadius: 1.5,
                                borderColor: 'warning.main',
                                bgcolor: 'warning.lighter'
                            }}
                        >
                            <Typography variant="body2" color="warning.dark">
                                This affiliate is not on premium plan. Only credit payment is available.
                            </Typography>
                        </Paper>
                    )}

                    {affiliateCredit > 0 && (
                        <Box sx={{
                            mb: 2,
                            overflow: 'visible'
                        }}>
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'nowrap',
                                mb: 1
                            }}>
                                <TextField
                                    label="Apply credit"
                                    value={creditInput}
                                    onChange={handleCreditInputChange}
                                    error={!!errorMessage}
                                    size="small"
                                    type="number"
                                    inputProps={{
                                        min: 0,
                                        max: maxApplicableCredit,
                                        step: "any"
                                    }}
                                    sx={{ flex: 1, minWidth: 0 }}
                                />
                                <Box sx={{
                                    display: 'flex',
                                    gap: 1,
                                    width: 'auto',
                                    flexShrink: 0
                                }}>
                                    <Button
                                        variant="contained"
                                        onClick={applyCredit}
                                        disabled={!!errorMessage || creditInput === '' || Number(creditInput) === appliedCredit}
                                        size="small"
                                        sx={{
                                            fontWeight: '500',
                                            minWidth: { xs: 66, sm: 80 }
                                        }}
                                    >
                                        Apply
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={applyMax}
                                        size="small"
                                        sx={{
                                            fontWeight: '500',
                                            minWidth: { xs: 52, sm: 64 }
                                        }}
                                    >
                                        Max
                                    </Button>
                                </Box>
                            </Box>
                            <Typography
                                variant="caption"
                                color={errorMessage ? 'error' : 'text.secondary'}
                                sx={{ display: 'block', mb: 0.5 }}
                            >
                                {creditHelperMessage}
                            </Typography>

                            {appliedCredit > 0 && (
                                <Box sx={{
                                    p: 1.5,
                                    bgcolor: 'success.lighter',
                                    color: 'success.dark',
                                    borderRadius: 1,
                                    mt: 2
                                }}>
                                    <Typography variant="body2" sx={{ fontWeight: '500' }}>
                                        Credit applied: {formatCurrency(appliedCredit, paymentCurrency)} (Remaining to pay: {formatCurrency(Math.max(priceAfterDiscount - appliedCredit, 0), paymentCurrency)})
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                </Box>

                {!affiliateCredit && isPremium && (
                    <Paper
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            overflow: 'visible'
                        }}
                    >
                        <AccountBalanceIcon sx={{ mr: 2, color: 'text.secondary' }} />
                        <Typography>
                            You'll be redirected to Montonio to complete your payment.
                        </Typography>
                    </Paper>
                )}



            </CardContent>
        </Card>
    );
}

PaymentForm.propTypes = {
    affiliateCredit: PropTypes.number,
    appliedCredit: PropTypes.number,
    setAppliedCredit: PropTypes.func.isRequired,
    planPrice: PropTypes.number.isRequired,
    affiliateInfo: PropTypes.shape({
        name: PropTypes.string.isRequired,
        logo: PropTypes.string.isRequired,
        paymentCurrency: PropTypes.string
    }).isRequired,
    discountCode: PropTypes.string,
    setDiscountCode: PropTypes.func,
    appliedDiscount: PropTypes.object,
    setAppliedDiscount: PropTypes.func,
    affiliateId: PropTypes.number,
    planId: PropTypes.number,
    isPremiumAffiliate: PropTypes.bool
};

PaymentForm.defaultProps = {
    affiliateCredit: 0,
    appliedCredit: 0,
    discountCode: '',
    appliedDiscount: null
};